import { applicationPagePresentation } from '../../shared/content/applicationPageContent.js';
import { faqSection } from '../sections/faqSection.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { highlightedHeading } from '../components/highlightedHeading.js';
import { sectionHeading } from '../components/sectionHeading.js';
import { contactMap } from '../components/contactMap.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import type { LandingContent, LandingLocale } from '../../shared/content/landingContent.js';
import {
  staticPageContentRepository,
  type StaticPageAudience,
  type StaticPageContent as StaticPageData,
  type StaticPageKey,
} from '../../shared/content/staticPageContent.js';
import { staticPageUiCopy } from '../../shared/content/staticPageLocalization.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  elementLinkClickHandler,
  elementLinkKeyDownHandler,
  syncLinkedElementAccessibility,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { syncInlineTextTypography } from '../editor/landingEditorInlineTypography.js';
import { LandingEditorClient } from '../editor/landingEditorClient.js';
import { landingProjectContentRepository } from '../editor/landingProjectContent.js';
import {
  createDefaultLandingEditorProject,
  getDefaultStaticPageBuiltInSectionOrder,
  getOrderedStaticPageBuiltInSections,
  getOrderedStaticSections,
  type LandingHomeSectionKey,
  type StaticPageBuiltInSectionKey,
} from '../../shared/editor/landingEditorTypes.js';
import { footerSection } from '../sections/footerSection.js';
import { renderLandingHomeSection } from '../sections/homeSections.js';
import { industryHeroSection, isIndustryHeroPage } from '../sections/industryHeroSection.js';
import { imprintHeroSection } from '../sections/imprintHeroSection.js';
import { landingNavigation, openCompanyContactDialog } from '../sections/navigation.js';
import { industryPageForAudience } from '../../shared/content/industryPageActions.js';
import { pathJourney } from '../sections/pathJourney.js';
import { globusJobSections } from '../sections/globusJobSections.js';
import { LandingLocaleState } from '../state/landingLocaleState.js';

interface StaticPageRenderOptions {
  pageKey: StaticPageKey;
  content: LandingContent;
  currentLocale: LandingLocale;
  onLanguageChange: (locale: LandingLocale) => void;
  audience: StaticPageAudience;
  onAudienceChange: (audience: StaticPageAudience) => void;
  editor?: LandingEditorRenderOptions;
  className?: string;
  page?: StaticPageData;
  sections?: LandingHomeSectionKey[];
  staticPageSectionOrder?: StaticPageBuiltInSectionKey[];
}

import {
  renderContactAction,
  renderContactHero,
  renderGlobusHero,
  renderImageCardSection,
  renderMissionStory,
  renderTeamGroups,
  renderTeamHero,
  renderTeamMemberCard,
} from './staticPageRenderers.js';
export const staticPageContent = ({ pageKey, content, currentLocale, onLanguageChange, audience, onAudienceChange, editor, className = 'vp-page', page: pageOverride, sections, staticPageSectionOrder }: StaticPageRenderOptions) => {
  const sourcePage = pageOverride ?? staticPageContentRepository.getPage(pageKey, audience);
  const page = pageKey === 'bewerben' ? applicationPagePresentation(sourcePage, content) : industryPageForAudience(sourcePage, audience, currentLocale);
  const uiCopy = staticPageUiCopy(currentLocale);
  const contactDetails = page.contact;
  const prefix = `static.${pageKey}`;
  // Modular landing sections that an editor dragged onto this sub-page (e.g. the employer hero or the journey timeline).
  const extraSections = sections ?? [];
  // The Ablauf page gets a dedicated hero and the playful winding-path layout instead of the default card grid.
  const isAblauf = page.key === 'ablauf';
  // The mission page uses the same data model, but a calmer centered hero treatment.
  const isMissionPage = page.key === 'ueber-vivaporta';
  const isTeamPage = page.key === 'team';
  const isGlobusPage = page.key === 'globus';
  const isContactPage = page.key === 'kontakt';
  const isImprintPage = page.key === 'impressum';
  const isApplicationPage = page.key === 'bewerben';
  // Industry pages share the compact, editable photo-card hero.
  const isIndustryHero = isIndustryHeroPage(page.key);
  const heroPage = page;
  const liftedImageCardSections =
    page.key === 'einzelhandel' || page.key === 'globus' ? (page.sections?.map((section, sectionIndex) => ({ section, sectionIndex })).filter(({ section }) => section.cards?.some(card => card.image)) ?? []) : [];
  const defaultStaticPageSectionOrder = getDefaultStaticPageBuiltInSectionOrder(page);
  const orderedStaticPageSections = [...(staticPageSectionOrder ?? []), ...defaultStaticPageSectionOrder.filter(section => !(staticPageSectionOrder ?? []).includes(section))];
  const sectionOrder = (section: StaticPageBuiltInSectionKey) => {
    const index = orderedStaticPageSections.indexOf(section);
    return index >= 0 ? index : orderedStaticPageSections.length;
  };
  const sectionOrderStyle = (section: StaticPageBuiltInSectionKey, elementId: string) => {
    const baseStyle = editorNodeStyle(editor, elementId);
    return `${baseStyle}${baseStyle ? ' ' : ''}order: ${sectionOrder(section)};`;
  };
  const afterHeroSectionStyle = (elementId: string) => {
    const baseStyle = editorNodeStyle(editor, elementId);
    const heroIndex = orderedStaticPageSections.indexOf('hero');
    return `${baseStyle}${baseStyle ? ' ' : ''}order: ${heroIndex >= 0 ? heroIndex + 1 : 1};`;
  };
  const missionStorySectionStyle = (elementId: string) => {
    const baseStyle = editorNodeStyle(editor, elementId);
    return `${baseStyle}${baseStyle ? ' ' : ''}order: ${sectionOrder('missionStory')};`;
  };

  return html`
    <div
      class=${className}
      @click=${elementLinkClickHandler(editor)}
      @keydown=${elementLinkKeyDownHandler(editor)}
    >
      ${editor?.mode === 'editor'
        ? ''
        : html`<a class="vp-skip-link" href="#main-content">
            ${currentLocale === 'tr'
              ? 'İçeriğe geç'
              : currentLocale === 'en'
                ? 'Skip to content'
                : 'Zum Inhalt springen'}
          </a>`}
      ${landingNavigation({ content, currentLocale, onLanguageChange, audience, onAudienceChange, editor })}
      <main
        class=${`vp-static-page ${isImprintPage ? 'vp-static-page-imprint' : ''} ${isApplicationPage ? 'vp-application-page' : ''}`}
        id=${editor?.mode === 'editor' ? '' : 'main-content'}
        tabindex=${editor?.mode === 'editor' ? '0' : '-1'}
      >
        ${isImprintPage
          ? imprintHeroSection({ page, editor, sectionStyle: sectionOrderStyle('hero', `${prefix}.hero`) })
          : isIndustryHero
          ? industryHeroSection({
              pageKey,
              page: heroPage,
              locale: currentLocale,
              audience,
              editor,
              sectionStyle: sectionOrderStyle('hero', `${prefix}.hero`),
            })
          : isTeamPage
            ? renderTeamHero(page, prefix, sectionOrderStyle('hero', `${prefix}.teamHero`), currentLocale, editor)
            : isGlobusPage
              ? renderGlobusHero(page, prefix, sectionOrderStyle('hero', `${prefix}.hero`), editor)
              : isContactPage
                ? renderContactHero(page, prefix, currentLocale, sectionOrderStyle('hero', `${prefix}.hero`), editor)
                : html`<section
                    class=${editorNodeClass(editor, `${prefix}.hero`, `vp-static-hero ${isAblauf ? 'vp-static-hero-ablauf' : ''} ${isMissionPage ? 'vp-static-hero-mission' : ''}`)}
                    style=${sectionOrderStyle('hero', `${prefix}.hero`)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero`, event)}
                  >
                    ${isAblauf ? html`<span class="vp-ablauf-hero-glow" aria-hidden="true"></span>` : ''}
                    ${isMissionPage
                      ? html`<div class="vp-team-hero-lines vp-mission-hero-lines" aria-hidden="true"><span></span><span></span><span></span></div>`
                      : ''}
                    <div
                      class=${editorNodeClass(editor, `${prefix}.hero.layout`, `vp-shell vp-static-hero-layout ${page.heroImage ? '' : 'vp-static-hero-layout-text-only'} ${isMissionPage ? 'vp-static-hero-layout-mission' : ''}`)}
                      style=${editorNodeStyle(editor, `${prefix}.hero.layout`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero.layout`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero.layout`, event)}
                    >
                      <div
                        class=${editorNodeClass(editor, `${prefix}.hero.copy`)}
                        style=${editorNodeStyle(editor, `${prefix}.hero.copy`)}
                        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero.copy`, event)}
                        @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero.copy`, event)}
                      >
                        ${isMissionPage
                          ? html`<div class="vp-team-hero-word vp-mission-hero-word" aria-hidden="true">Mission</div>`
                          : ''}
                        <p
                          class=${editorNodeClass(editor, `${prefix}.hero.eyebrow`, 'vp-eyebrow')}
                          style=${editorNodeStyle(editor, `${prefix}.hero.eyebrow`)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero.eyebrow`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero.eyebrow`, event)}
                        >
                          ${page.eyebrow}
                        </p>
                        ${(isMissionPage || isApplicationPage)
                          ? highlightedHeading({
                              title: page.title,
                              highlight: page.heroTitleHighlight,
                              editor,
                              titleElementId: `${prefix}.hero.title`,
                              highlightElementId: `${prefix}.hero.titleHighlight`,
                            })
                          : editorSemanticText({ editor, elementId: `${prefix}.hero.title`, fallbackTag: 'h1', content: page.title })}
                        ${editorSemanticText({ editor, elementId: `${prefix}.hero.intro`, fallbackTag: 'p', content: page.intro })}
                        ${isApplicationPage && page.heroAction ? html`
                          <div class="vp-section-actions">
                            <a class=${editorNodeClass(editor, `${prefix}.hero.heroAction.label`, 'vp-action vp-action-primary')}
                              style=${editorNodeStyle(editor, `${prefix}.hero.heroAction.label`)} href=${page.heroAction.href}
                              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero.heroAction.label`, event)}
                              @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero.heroAction.label`, event)}
                            >${page.heroAction.label}<span class="icon-[material-symbols--arrow-forward]" aria-hidden="true"></span></a>
                          </div>
                          <ul class="vp-application-trust">${(page.heroHighlights ?? []).map((text, index) => html`
                            <li>${editorSemanticText({ editor, elementId: `${prefix}.hero.heroHighlight.${index}`, fallbackTag: 'p', content: text })}</li>
                          `)}</ul>
                        ` : ''}
                        ${isAblauf
                          ? html`<div class="vp-ablauf-hero-meta">
                              <span class="vp-ablauf-hero-pill"><span class="icon-[material-symbols--conversion-path]"></span>${uiCopy.processStages(page.cards.length)}</span>
                              <span class="vp-ablauf-hero-pill"><span class="icon-[material-symbols--diversity-1]"></span>${uiCopy.personallyGuided}</span>
                              <span class="vp-ablauf-hero-pill"><span class="icon-[material-symbols--flag]"></span>${uiCopy.untilFirstDay}</span>
                            </div>`
                          : ''}
                      </div>
                      ${page.heroImage
                        ? html`<img
                            class=${editorNodeClass(editor, `${prefix}.hero.heroImage`)}
                            style=${editorNodeStyle(editor, `${prefix}.hero.heroImage`)}
                            src=${page.heroImage}
                            alt=${page.title}
                            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.hero.heroImage`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.hero.heroImage`, event)}
                          />`
                        : ''}
                    </div>
                  </section>`}
        ${isMissionPage ? renderMissionStory(page, prefix, missionStorySectionStyle(`${prefix}.missionStory`), editor) : ''}
        ${liftedImageCardSections.map(({ section, sectionIndex }) =>
          renderImageCardSection(
            pageKey,
            prefix,
            section,
            sectionIndex,
            isGlobusPage
              ? sectionOrderStyle(`detail:${sectionIndex}`, `${prefix}.section.${sectionIndex}`)
              : afterHeroSectionStyle(`${prefix}.section.${sectionIndex}`),
            currentLocale,
            editor,
          ),
        )}
        ${page.cards.length && page.key !== 'kontakt'
          ? html`
              <section
                class=${editorNodeClass(editor, `${prefix}.cards`, `vp-section vp-static-content-section ${isAblauf ? 'vp-ablauf-section' : ''}`)}
                style=${sectionOrderStyle('cards', `${prefix}.cards`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.cards`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.cards`, event)}
              >
                <div class="vp-shell">
                  ${isImprintPage ? html`<div class="vp-section-heading vp-section-heading-center">
                    ${editorSemanticText({ editor, elementId: page.cardsTitle !== undefined ? `${prefix}.cards.title` : `${prefix}.hero.title`, fallbackTag: 'h2', content: page.cardsTitle ?? page.title })}
                  </div>` : sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1',
                    eyebrow: isAblauf ? uiCopy.processEyebrow : page.cardsEyebrow ?? page.eyebrow,
                    title: isAblauf ? uiCopy.processTitle : page.cardsTitle ?? page.title,
                    ...(isIndustryHero ? { editor, elementPrefix: `${prefix}.cards` } : {}),
                    align: 'center',
                  })}
                  ${isAblauf
                    ? pathJourney({
                        items: page.cards,
                        ids: {
                          card: index => `${prefix}.card.${index}`,
                          icon: index => `${prefix}.card.${index}.icon`,
                          title: index => `${prefix}.card.${index}.title`,
                          text: index => `${prefix}.card.${index}.text`,
                          image: index => `${prefix}.card.${index}.image`,
                        },
                        containerId: `${prefix}.cards.container`,
                        editor,
                      })
                    : html`
                        <div class="vp-card-grid vp-card-grid-three">
                          ${page.cards.map((card, index) => {
                            const cardId = `${prefix}.card.${index}`;
                            return html`
                              <article
                                class=${editorNodeClass(editor, cardId, 'vp-info-card vp-info-card-light reveal-card')}
                                style=${editorNodeStyle(editor, cardId)}
                                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cardId, event)}
                                @click=${(event: MouseEvent) => editorNodeClick(editor, cardId, event)}
                              >
                                <span
                                  class=${editorNodeClass(editor, `${cardId}.icon`, card.iconClass)}
                                  style=${editorNodeStyle(editor, `${cardId}.icon`)}
                                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.icon`, event)}
                                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.icon`, event)}
                                ></span>
                                ${editorSemanticText({ editor, elementId: `${cardId}.title`, fallbackTag: 'h3', content: card.title })}
                                ${editorSemanticText({ editor, elementId: `${cardId}.text`, fallbackTag: 'p', content: card.text })}
                              </article>
                            `;
                          })}
                        </div>
                      `}
                </div>
              </section>
            `
          : ''}
        ${page.sections?.length
          ? page.sections.map((section, sectionIndex) =>
              liftedImageCardSections.some(entry => entry.sectionIndex === sectionIndex)
                ? ''
                : section.cards?.some(card => card.image)
                  ? renderImageCardSection(pageKey, prefix, section, sectionIndex, sectionOrderStyle(`detail:${sectionIndex}`, `${prefix}.section.${sectionIndex}`), currentLocale, editor)
                  : html`
                      <section
                        class=${editorNodeClass(editor, `${prefix}.section.${sectionIndex}`, `vp-section vp-static-detail-section ${section.image ? 'vp-static-detail-section-media' : ''}`)}
                        style=${sectionOrderStyle(`detail:${sectionIndex}`, `${prefix}.section.${sectionIndex}`)}
                        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.section.${sectionIndex}`, event)}
                        @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.section.${sectionIndex}`, event)}
                      >
                        <div class="vp-shell">
                          ${section.image
                            ? html`
                                <div class="vp-static-detail-media-layout ${isGlobusPage && section.imageAlt?.toLowerCase().includes('globus logo') ? 'vp-static-detail-media-layout-logo-right' : ''}">
                                  <figure class="vp-static-detail-media">
                                    <img
                                      class=${editorNodeClass(editor, `${prefix}.section.${sectionIndex}.image`)}
                                      style=${editorNodeStyle(editor, `${prefix}.section.${sectionIndex}.image`)}
                                      src=${section.image}
                                      alt=${section.imageAlt ?? section.title}
                                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.section.${sectionIndex}.image`, event)}
                                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.section.${sectionIndex}.image`, event)}
                                    />
                                  </figure>
                                  ${sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1',
                                    eyebrow: section.eyebrow,
                                    title: section.title,
                                    text: section.text,
                                    align: 'left',
                                    editor,
                                    elementPrefix: `static.${pageKey}.section.${sectionIndex}`,
                                  })}
                                </div>
                              `
                            : sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1',
                                eyebrow: section.eyebrow,
                                title: section.title,
                                text: section.text,
                                align: section.cards?.length ? 'center' : 'left',
                                editor,
                                elementPrefix: `static.${pageKey}.section.${sectionIndex}`,
                              })}
                          ${section.cards?.length
                            ? html`
                                <div
                                  class="vp-card-grid ${section.cards.length === 4
                                    ? 'vp-card-grid-four'
                                    : 'vp-card-grid-three'}"
                                >
                                  ${section.cards.map((card, cardIndex) => {
                                    const cardId = `${prefix}.section.${sectionIndex}.card.${cardIndex}`;
                                    return html`
                                      <article
                                        class=${editorNodeClass(editor, cardId, 'vp-info-card vp-info-card-light reveal-card')}
                                        style=${editorNodeStyle(editor, cardId)}
                                        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cardId, event)}
                                        @click=${(event: MouseEvent) => editorNodeClick(editor, cardId, event)}
                                      >
                                        <span
                                          class=${editorNodeClass(editor, `${cardId}.icon`, card.iconClass)}
                                          style=${editorNodeStyle(editor, `${cardId}.icon`)}
                                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.icon`, event)}
                                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.icon`, event)}
                                        ></span>
                                        ${editorSemanticText({ editor, elementId: `${cardId}.title`, fallbackTag: 'h3', content: card.title })}
                                        ${editorSemanticText({ editor, elementId: `${cardId}.text`, fallbackTag: 'p', content: card.text })}
                                      </article>
                                    `;
                                  })}
                                </div>
                              `
                            : ''}
                        </div>
                      </section>
                    `,
            )
          : ''}
        ${audience === 'applicant' && page.globusJobs?.length
          ? globusJobSections({
              page,
              locale: currentLocale,
              sectionStyle: sectionOrderStyle('globusJobs', `${prefix}.globusJobs`),
              editor,
            })
          : ''}
        ${isApplicationPage
          ? faqSection('faqApplicant', content, editor, { headingTag: 'h2', style: sectionOrderStyle('faq', 'section.faqApplicant') })
          : page.faq?.length
          ? html`
              <section
                class=${editorNodeClass(editor, `${prefix}.faq`, 'vp-section vp-static-faq-section')}
                style=${sectionOrderStyle('faq', `${prefix}.faq`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.faq`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.faq`, event)}
              >
                <div class="vp-shell">
                  ${sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1', eyebrow: uiCopy.faqEyebrow, title: uiCopy.faqTitle, align: 'center' })}
                  <div class="vp-static-faq-list">
                    ${page.faq.map((item, index) => {
                      const itemId = `${prefix}.faq.${index}`;
                      return html`
                        <details
                          class=${editorNodeClass(editor, itemId, 'vp-static-faq-item')}
                          style=${editorNodeStyle(editor, itemId)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, itemId, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, itemId, event)}
                        >
                          <summary
                            class=${editorNodeClass(editor, `${itemId}.question`)}
                            style=${editorNodeStyle(editor, `${itemId}.question`)}
                            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.question`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.question`, event)}
                          >
                            ${item.question}
                          </summary>
                          <p
                            class=${editorNodeClass(editor, `${itemId}.answer`)}
                            style=${editorNodeStyle(editor, `${itemId}.answer`)}
                            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${itemId}.answer`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${itemId}.answer`, event)}
                          >
                            ${item.answer}
                          </p>
                        </details>
                      `;
                    })}
                  </div>
                </div>
              </section>
            `
          : ''}
        ${page.cta
          ? html`
              <section
                class=${editorNodeClass(editor, `${prefix}.cta`, 'vp-section vp-static-cta-section')}
                style=${sectionOrderStyle('cta', `${prefix}.cta`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.cta`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.cta`, event)}
              >
                <div class="vp-shell">
                  <div class=${`vp-static-cta-panel reveal-card ${page.cta.image ? 'vp-static-cta-panel-media' : ''}`}>
                    <div class="vp-static-cta-copy">
                      ${sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1',
                        eyebrow: page.cta.eyebrow,
                        title: page.cta.title,
                        text: page.cta.text,
                        editor,
                        elementPrefix: `static.${pageKey}.cta`,
                      })}
                      <div class="vp-section-actions">
                      ${(isGlobusPage || isIndustryHero) && audience === 'company'
                        ? html`<button
                            class=${editorNodeClass(editor, `${prefix}.cta.primaryAction.label`, 'vp-action vp-action-primary')}
                            style=${editorNodeStyle(editor, `${prefix}.cta.primaryAction.label`)}
                            type="button"
                            aria-haspopup="dialog"
                            aria-controls="vp-contact-modal"
                            @pointerdown=${(event: PointerEvent) =>
                              editorNodePointerDown(editor, `${prefix}.cta.primaryAction.label`, event)}
                            @click=${(event: MouseEvent) => {
                              if (editor?.mode === 'editor') {
                                editorNodeClick(editor, `${prefix}.cta.primaryAction.label`, event);
                                return;
                              }
                              event.stopPropagation();
                              openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
                            }}
                          >
                            ${page.cta.primaryAction.label}
                          </button>`
                        : html`<a
                            class=${editorNodeClass(editor, `${prefix}.cta.primaryAction.label`, 'vp-action vp-action-primary')}
                            style=${editorNodeStyle(editor, `${prefix}.cta.primaryAction.label`)}
                            href=${page.cta.primaryAction.href}
                            @pointerdown=${(event: PointerEvent) =>
                              editorNodePointerDown(editor, `${prefix}.cta.primaryAction.label`, event)}
                            @click=${(event: MouseEvent) =>
                              editorNodeClick(editor, `${prefix}.cta.primaryAction.label`, event)}
                          >
                            ${page.cta.primaryAction.label}
                          </a>`}
                      <a
                        class=${editorNodeClass(editor, `${prefix}.cta.secondaryAction.label`, 'vp-action vp-action-secondary')}
                        style=${editorNodeStyle(editor, `${prefix}.cta.secondaryAction.label`)}
                        href=${page.cta.secondaryAction.href}
                        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.cta.secondaryAction.label`, event)}
                        @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.cta.secondaryAction.label`, event)}
                        >${page.cta.secondaryAction.label}</a
                      >
                      </div>
                    </div>
                    ${page.cta.image
                      ? html`<figure class="vp-static-cta-media">
                          <img
                            class=${editorNodeClass(editor, `${prefix}.cta.image`)}
                            style=${editorNodeStyle(editor, `${prefix}.cta.image`)}
                            src=${page.cta.image}
                            alt=${page.cta.imageAlt ?? page.cta.title}
                            loading="lazy"
                            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.cta.image`, event)}
                            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.cta.image`, event)}
                          />
                        </figure>`
                      : ''}
                  </div>
                </div>
              </section>
            `
          : ''}
        ${page.team?.length
          ? html`
              <section
                class=${editorNodeClass(editor, `${prefix}.team`, 'vp-section vp-team-section')}
                style=${sectionOrderStyle('team', `${prefix}.team`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.team`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.team`, event)}
              >
                <div class="vp-shell">
                  ${sectionHeading({
                    headingTag: isApplicationPage || isIndustryHero ? 'h2' : 'h1',
                    eyebrow: isTeamPage ? uiCopy.teamSectionEyebrow : page.eyebrow,
                    title: isTeamPage ? uiCopy.teamSectionTitle : page.title,
                    text: isTeamPage ? uiCopy.teamSectionText : page.intro,
                  })}
                  ${isTeamPage ? renderTeamGroups(page, prefix, currentLocale, editor) : html`<div class="vp-team-grid">${page.team.map((member, index) => renderTeamMemberCard(member, index, prefix, currentLocale, editor))}</div>`}
                </div>
              </section>
            `
          : ''}
        ${contactDetails
          ? html`
              <section
                class=${editorNodeClass(editor, `${prefix}.contact`, 'vp-contact-section vp-contact-page-section')}
                style=${sectionOrderStyle('contact', `${prefix}.contact`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.contact`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.contact`, event)}
              >
                <div class="vp-shell">
                  <div class="vp-contact-page-copy">
                    <p
                      class=${editorNodeClass(editor, `${prefix}.contactEyebrow`, 'vp-eyebrow')}
                      style=${editorNodeStyle(editor, `${prefix}.contactEyebrow`)}
                      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.contactEyebrow`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.contactEyebrow`, event)}
                    >
                      ${contactDetails.eyebrow}
                    </p>
                    ${editorSemanticText({ editor, elementId: `${prefix}.contactTitle`, fallbackTag: 'h2', content: contactDetails.title })}
                    ${editorSemanticText({ editor, elementId: `${prefix}.contactText`, fallbackTag: 'p', content: contactDetails.text })}
                    <div class="vp-contact-quick-grid">${page.cards.map((card, index) => renderContactAction(pageKey, index, card.title, card.iconClass, card.text, contactDetails, editor))}</div>
                  </div>
                  ${contactMap({ contact: contactDetails, prefix, locale: currentLocale, scope: `${pageKey}:${audience}:${currentLocale}`, editor })}
                </div>
              </section>
            `
          : ''}
        ${editor && extraSections.length ? extraSections.map((section, index) =>
          isIndustryHero && (section === 'faqApplicant' || section === 'faqCompany')
            ? faqSection(section, content, editor, {
                headingTag: 'h2',
                style: `${editorNodeStyle(editor, `section.${section}`)} order: ${orderedStaticPageSections.length + index};`,
              })
            : renderLandingHomeSection(section, content, editor),
        ) : ''}
      </main>
      ${footerSection(content, editor)}
    </div>
  `;
};

class StaticPageViewModel {
  constructor(private readonly pageKey: StaticPageKey) {}

  render() {
    return stateful(() => {
      const [locale, setLocale] = useState<LandingLocale>(LandingLocaleState.getInitialLocale());
      const [audience, setAudience] = useState<StaticPageAudience>(this.initialAudience(), []);
      const [project, setProject] = useState(createDefaultLandingEditorProject(), []);
      const content = landingProjectContentRepository.landing(project, locale);
      const editorRenderOptions: LandingEditorRenderOptions = { project };

      const handleLanguageChange = (nextLocale: LandingLocale) => {
        setLocale(nextLocale);
        LandingLocaleState.change(nextLocale);
      };

      useEffect(() => LandingLocaleState.subscribe(setLocale, setAudience), []);

      useEffect(() => {
        let active = true;
        void LandingEditorClient.loadPublished()
          .then(project => {
            if (active) setProject(project);
          })
          .catch(() => {});
        return () => {
          active = false;
        };
      }, []);

      useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
          syncLinkedElementAccessibility(project);
          syncInlineTextTypography(project, locale);
        });
        return () => window.cancelAnimationFrame(frame);
      }, [project, locale, audience]);

      LandingLocaleState.persist(locale, landingProjectContentRepository.staticPage(project, locale, audience, this.pageKey).intro);

      return staticPageContent({
        pageKey: this.pageKey,
        page: landingProjectContentRepository.staticPage(project, locale, audience, this.pageKey),
        content,
        currentLocale: locale,
        onLanguageChange: handleLanguageChange,
        audience,
        onAudienceChange: nextAudience => {
          setAudience(nextAudience);
          LandingLocaleState.changeAudience(nextAudience);
        },
        editor: editorRenderOptions,
        sections: getOrderedStaticSections(project, this.pageKey, audience),
        staticPageSectionOrder: getOrderedStaticPageBuiltInSections(project, this.pageKey, audience),
      });
    })();
  }

  private initialAudience(): StaticPageAudience {
    return LandingLocaleState.getAudience();
  }
}

export const StaticPage = (pageKey: StaticPageKey) => new StaticPageViewModel(pageKey).render();
